import React, { useState } from 'react'
import styled from "styled-components";
import colors from "theme/colors";
import Text from 'components/_UI/Text';
import Fieldset from 'components/_UI/Fieldset';
import { useForm } from 'react-hook-form';
import userApi from 'api/requests/user';
import fieldValidations from 'helpers/fieldValidations';
import Modal from 'components/_UI/Modal';
import Loader from 'components/_UI/Loader';
import { useOnSelectMenu } from 'context/general/OnSelectMenu';
import { Lock, AlertCircle } from 'lucide-react';

export function UpdatePasswordDialog({ open, setOpen, setModalSucessPassword }) {

    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(false)
    const { setOnSelectMenu } = useOnSelectMenu();

    function closeModalChangePassword() {
        setOpen(false)
        setOnSelectMenu(false)
    }

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm()

    const changePassword = async ({ currentPassword, newPassword, confirmNewPassword }) => {

        if (newPassword !== confirmNewPassword) {
            setError('O campo nova senha e confirmação de senha devem ser iguais')
            return
        }
        setLoading(true)
        setError(false);

        try {
            await userApi.changePassword(currentPassword, newPassword)
            closeModalChangePassword()
            setModalSucessPassword(true)
        } catch ({ response }) {
            if (response?.status == 400) {
                setError('Senha atual inválida');
                return;
            }
            setError('Ocorreu um erro, tente novamente')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal isOpen={open} onClose={closeModalChangePassword}>
            <Container onSubmit={handleSubmit(changePassword)}>
                <ModalWrapper>
                    <Header>
                        <IconCircle>
                            <Lock size={20} color={colors.occasionalPurple} />
                        </IconCircle>
                        <Text name='highlight' type='bold' color={colors.nightDark}>
                            Alterar senha
                        </Text>
                        <Text name='small' color={colors.night}>
                            Informe sua senha atual e escolha uma nova senha.
                        </Text>
                    </Header>

                    <FieldGroup>
                        <Fieldset
                            layout='squired'
                            label='Senha atual'
                            name='currentPassword'
                            badgeIconPassword
                            badgeIconPositionPassword={'right'}
                            badgeIconColorPassword={colors.night}
                            inputType="password"
                            register={register}
                            validations={fieldValidations.passwordWithoutPatterns('senha atual')}
                        />
                        {errors?.currentPassword && (
                            <ErrorText>{errors.currentPassword.message}</ErrorText>
                        )}
                    </FieldGroup>

                    <FieldGroup>
                        <Fieldset
                            layout='squired'
                            label='Nova senha'
                            name='newPassword'
                            inputType={'password'}
                            badgeIconPassword
                            badgeIconColorPassword={colors.night}
                            badgeIconPositionPassword={'right'}
                            register={register}
                            validations={fieldValidations.password('nova senha')}
                        />
                        {errors?.newPassword && (
                            <ErrorText>{errors.newPassword.message}</ErrorText>
                        )}
                    </FieldGroup>

                    <FieldGroup>
                        <Fieldset
                            layout='squired'
                            label='Confirmar nova senha'
                            name='confirmNewPassword'
                            register={register}
                            badgeIconPassword
                            badgeIconColorPassword={colors.night}
                            badgeIconPositionPassword={'right'}
                            inputType={'password'}
                            validations={fieldValidations.password('confirmação de senha')}
                        />
                        {errors?.confirmNewPassword && (
                            <ErrorText>{errors.confirmNewPassword.message}</ErrorText>
                        )}
                    </FieldGroup>

                    {error && (
                        <ErrorBanner>
                            <AlertCircle size={16} color={colors.danger} />
                            <Text name='small' color={colors.danger}>{error}</Text>
                        </ErrorBanner>
                    )}

                    {loading ? (
                        <LoaderWrapper>
                            <Loader />
                        </LoaderWrapper>
                    ) : (
                        <ButtonsRow>
                            <PrimaryBtn type='submit'>
                                Confirmar alteração
                            </PrimaryBtn>
                            <SecondaryBtn type='button' onClick={closeModalChangePassword}>
                                Cancelar
                            </SecondaryBtn>
                        </ButtonsRow>
                    )}
                </ModalWrapper>
            </Container>
        </Modal>
    )
}

const Container = styled.form`
    width: 100%;
`;

const ModalWrapper = styled.div`
    width: 100%;
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 4px;
    margin-bottom: 16px;
`;

const IconCircle = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: #F3EAFF;
    margin-bottom: 2px;
`;

const FieldGroup = styled.div`
    margin-bottom: 2px;
`;

const ErrorText = styled.p`
    color: ${colors.danger};
    font-size: 12px;
    margin-top: 4px;
    padding-left: 2px;
`;

const ErrorBanner = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: #FEF2F2;
    border: 1px solid #FECACA;
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 16px;
`;

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 20px 0;
`;

const ButtonsRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
`;

const BaseBtn = styled.button`
    width: 100%;
    padding: 11px 20px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all .2s ease;
    border: none;
    text-align: center;
`;

const PrimaryBtn = styled(BaseBtn)`
    background: ${colors.occasionalPurple};
    color: ${colors.neutralLight};

    &:hover {
        opacity: 0.9;
        box-shadow: 0 4px 12px ${colors.occasionalPurple}40;
    }

    &:active {
        transform: scale(0.98);
    }
`;

const SecondaryBtn = styled(BaseBtn)`
    background: transparent;
    color: ${colors.night};
    border: 1.5px solid ${colors.neutralDark};

    &:hover {
        background: ${colors.neutralBackground};
        border-color: ${colors.nightLight};
    }
`;
